import { ElementType, PlanetType } from '@apeiron/planet'
import { styled } from '@mui/material'
import { PlanetTypeItem } from '@src/components/planet/detail/planetAboutSection/PlanetTypeItem'
import { usePlanetTypeContext } from '@src/contexts/planet/PlanetTypeContext'
import React from 'react'
import { useTranslation } from 'react-i18next'

const ContentContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-bottom: -20px;
`

const ElementSectionBase = styled('div')`
  display: flex;
  flex: 1;
  background-color: #1b1c20;
  padding: 10px 4px 10px 16px;
  margin-bottom: 20px;
`

const PureElementSection = styled(ElementSectionBase)`
  border: 0.6px solid;
  border-image: linear-gradient(#efdd7d, #000000 40%, #ee9a1c);
  border-image-slice: 1;
`

const DuoElementSection = styled(ElementSectionBase)`
  border: 0.6px solid;
  border-image: linear-gradient(#7f65b0, #415269 40%, #f2aaff);
  border-image-slice: 1;
`

const TriElementSection = styled(ElementSectionBase)`
  border: 0.6px solid;
  border-image: linear-gradient(#9c9c9c, #505050 40%, #cbcbcb);
  border-image-slice: 1;
`

const QuadElementSection = styled(ElementSectionBase)`
  border: 0.6px solid;
  border-image: linear-gradient(#1e1a1a, #1e2026);
  border-image-slice: 1;
`

const SectionTitleText = styled('div')`
  color: #ffffff;
  font-size: 16px;
  line-height: 18px;
  font-weight: 700;
  margin-right: 24px;
  align-self: center;
`

const ItemBaseContainer = styled('div')`
  display: flex;
  flex: 1;
  ${props => props.theme.breakpoints.down('res480')} {
    flex-wrap: wrap;
  }
`

const ItemContainer = styled(ItemBaseContainer)`
  display: flex;
  flex: 1;
  justify-content: space-between;
`

const QuadItemContainer = styled(ItemBaseContainer)`
  justify-content: space-evenly;
`

const DuoItemContainer = styled('div')`
  display: grid;
  flex: 1;
  grid-template-columns: repeat(3, 72px);
  grid-row-gap: 11px;
  justify-content: space-between;
  ${props => props.theme.breakpoints.down('res480')} {
    display: flex;
    flex-wrap: wrap;
  }
`

export const PlanetElementMap: React.FC<PlanetElementMapProps> = props => {
  const { className } = props

  const { t } = useTranslation()

  const { getPlanetTypeData } = usePlanetTypeContext()

  return (
    <ContentContainer className={className}>
      <PureElementSection>
        <SectionTitleText>
          {t('primeval_legacy_image.title.pure_element')}
        </SectionTitleText>
        <ItemContainer>
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Inferno)}
            elements={[ElementType.Fire]}
          />
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Leviathan)}
            elements={[ElementType.Water]}
          />
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Tempest)}
            elements={[ElementType.Air]}
          />
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Gigas)}
            elements={[ElementType.Earth]}
          />
        </ItemContainer>
      </PureElementSection>
      <DuoElementSection>
        <SectionTitleText>
          {t('primeval_legacy_image.title.duo_element')}
        </SectionTitleText>
        <DuoItemContainer>
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Tundra)}
            elements={[ElementType.Fire, ElementType.Water]}
          />
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Karsts)}
            elements={[ElementType.Air, ElementType.Earth]}
          />
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Volcanic)}
            elements={[ElementType.Fire, ElementType.Earth]}
          />
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Archipelago)}
            elements={[ElementType.Water, ElementType.Earth]}
          />
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Dunes)}
            elements={[ElementType.Fire, ElementType.Air]}
          />
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Delta)}
            elements={[ElementType.Water, ElementType.Air]}
          />
        </DuoItemContainer>
      </DuoElementSection>
      <TriElementSection>
        <SectionTitleText>
          {t('primeval_legacy_image.title.tri_element')}
        </SectionTitleText>
        <ItemContainer>
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Oasis)}
            elements={[ElementType.Fire, ElementType.Water, ElementType.Air]}
          />
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Wasteland)}
            elements={[ElementType.Fire, ElementType.Air, ElementType.Earth]}
          />
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Foresty)}
            elements={[ElementType.Water, ElementType.Air, ElementType.Earth]}
          />
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Mountainous)}
            elements={[ElementType.Fire, ElementType.Water, ElementType.Earth]}
          />
        </ItemContainer>
      </TriElementSection>
      <QuadElementSection>
        <SectionTitleText>
          {t('primeval_legacy_image.title.quad_element')}
        </SectionTitleText>
        <QuadItemContainer>
          <PlanetTypeItem
            planetType={getPlanetTypeData(PlanetType.Normal)}
            elements={[
              ElementType.Fire,
              ElementType.Water,
              ElementType.Air,
              ElementType.Earth
            ]}
          />
        </QuadItemContainer>
      </QuadElementSection>
    </ContentContainer>
  )
}

export type PlanetElementMapProps = {
  className?: string
}
